import { LoadingButton } from "@mui/lab";
import { Box, FormControl, FormLabel, Grid, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumb from "../../components/Breadcrumb";
import MainLayout from "../../components/MainLayout";
import ProtectedPermissionPage from "../../components/ProtectedPermissionPage";
import useNotification from "../../hooks/useNotification";
import regionService from "../../services/region";
import queryDefault from "../../utils/queryDefault";

export default function RegionEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const notification = useNotification();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm({
    defaultValues: {
      name: "",
    },
  });

  const { data: regionData } = useQuery(
    ["region-detail", id],
    () => regionService.viewRegion(String(id)),
    {
      ...queryDefault,
      enabled: Boolean(id),
    }
  );

  useEffect(() => {
    if (regionData?.data) {
      form.reset({
        name: regionData.data.name || "",
      });
    }
  }, [form, regionData]);

  const updateRegion = useMutation(regionService.updateRegion, {
    onSuccess: () => {
      setIsSubmitting(false);
      navigate("/app/region");
      notification.onOpen({
        message: "Region updated successfully!",
        type: "success",
        position: "top",
      });
    },
    onError: () => {
      setIsSubmitting(false);
      notification.onOpen({
        message: "Failed to update region",
        type: "error",
        position: "top",
      });
    },
    ...queryDefault,
  });

  const onSubmit = (values: any) => {
    if (!id) return;
    setIsSubmitting(true);
    updateRegion.mutate({ id, name: values.name });
  };

  return (
    <MainLayout>
      <Helmet>
        <title>Edit Region</title>
      </Helmet>

      <Breadcrumb
        label="Edit Region"
        breadcrumbs={[
          { label: "Dashboard", href: "/app" },
          { label: "Region Management", href: "/app/region" },
        ]}
      />

      <ProtectedPermissionPage acceptPermissions={[1, 2]}>
        <Box sx={{ mt: 3 }}>
          <Grid container>
            <Grid item lg={4} xs={12}>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required>Name</FormLabel>
                <Controller
                  name="name"
                  control={form.control}
                  rules={{ required: "Name is required" }}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <TextField
                      {...field}
                      inputRef={ref}
                      error={Boolean(fieldState.error?.message)}
                      helperText={fieldState.error?.message}
                      size="small"
                    />
                  )}
                />
              </FormControl>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ mt: 3 }}>
          <LoadingButton
            variant="contained"
            loading={isSubmitting}
            onClick={form.handleSubmit(onSubmit)}
          >
            Save
          </LoadingButton>
        </Box>
      </ProtectedPermissionPage>
    </MainLayout>
  );
}